/**
 * Hoopoe Agent Chat Widget — Side panel chat component for the APAQ Portal UI.
 *
 * Renders a floating chat button that opens a resizable side panel on the
 * right side of the screen (similar to an IDE agent panel). Sends messages
 * to the /chat API endpoint and displays AI-powered responses.
 *
 * The main page content shrinks to accommodate the panel via an
 * onLayoutChange callback passed from the parent.
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Fab,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AttachFile as AttachFileIcon,
} from '@material-ui/icons';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import ReactMarkdown from 'react-markdown';
import { Auth } from 'aws-amplify';

// ============================================================================
// Configuration
// ============================================================================
const CHAT_API_URL_MAP: Record<string, string> = {
  'localhost': 'https://uchqsrh9s5.execute-api.us-west-2.amazonaws.com/alpha/chat',
  'localhost.a2z.com': 'https://uchqsrh9s5.execute-api.us-west-2.amazonaws.com/alpha/chat',
  'portal.alpha.apaq.wolfgang.a2z.com': 'https://uchqsrh9s5.execute-api.us-west-2.amazonaws.com/alpha/chat',
  'portal.beta.apaq.wolfgang.a2z.com': 'https://k1brbx9z75.execute-api.us-west-2.amazonaws.com/beta/chat',
};

const getChatApiUrl = (): string | undefined => {
  const hostname = window.location.hostname;
  if (!(hostname in CHAT_API_URL_MAP)) {
    console.warn(`ChatWidget: no API URL configured for hostname "${hostname}"`);
    return undefined;
  }
  return CHAT_API_URL_MAP[hostname];
};

// Evaluated once at module load — hostname doesn't change at runtime
const CHAT_API_URL = getChatApiUrl();

// ============================================================================
// Auth helper
// ============================================================================
class AuthError extends Error {
  public readonly cause: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = 'AuthError';
    this.cause = cause;
  }
}

const getAuthToken = async (): Promise<string> => {
  try {
    const session = await Auth.currentSession();
    return session.getIdToken().getJwtToken();
  } catch (err) {
    throw new AuthError('Session expired or user unauthenticated', err);
  }
};

// ============================================================================
// Image attachment helpers
// ============================================================================
class ImageAttachError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ImageAttachError';
  }
}

// Read a Blob/File into a full data URL (data:<type>;base64,<...>)
const readFileAsDataUrl = (file: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Unexpected FileReader result type'));
      }
    };
    reader.onerror = () =>
      reject(new Error(`Failed to read image file${reader.error?.name ? `: ${reader.error.name}` : ''}`));
    reader.readAsDataURL(file);
  });

// Load an <img> element from a data URL so we can measure/redraw it.
// A 10s timeout rejects (and aborts img.src) so a hung/oversized decode can't leave
// the promise pending forever with no user feedback.
const IMAGE_DECODE_TIMEOUT_MS = 10_000;
const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    let settled = false;
    // Settle exactly once: whichever of timeout / onload / onerror fires first wins;
    // clearing img.src in the timeout path can synchronously re-fire onerror, so the
    // guard makes that a no-op rather than a redundant reject.
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      fn();
    };
    const timer = setTimeout(
      () => finish(() => { img.src = ''; reject(new Error('Image decode timed out')); }),
      IMAGE_DECODE_TIMEOUT_MS,
    );
    img.onload = () => finish(() => resolve(img));
    img.onerror = () => finish(() => reject(new Error('Failed to decode image')));
    img.src = src;
  });

// Split a data URL into its actual media type and the raw base64 payload.
const splitDataUrl = (dataUrl: string): { mediaType: string; data: string } => {
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx < 0) throw new ImageAttachError('Could not parse image data');
  const header = dataUrl.slice(0, commaIdx); // e.g. "data:image/png;base64"
  // Guard against a canvas/browser returning URL-encoded (non-base64) data — the
  // payload is only valid base64 when the header declares ;base64, otherwise the
  // backend would receive garbage bytes.
  if (!header.includes(';base64')) {
    throw new ImageAttachError('Could not encode image (unexpected data format).');
  }
  const match = /^data:([^;,]+)/.exec(header);
  // Defensive fallback only: a header that passed the ;base64 check above always
  // matches this regex, so 'image/png' is effectively unreachable. The result is
  // re-validated by isAllowedImageType at the call site regardless.
  return { mediaType: match ? match[1] : 'image/png', data: dataUrl.slice(commaIdx + 1) };
};

// Image attachment limits (declared before processImageFile, their first consumer)
const MAX_IMAGES = 3;
const MAX_IMAGE_DIMENSION = 1568; // px — longest edge after client-side downscale
// Two-stage size gating:
//  1) MAX_RAW_FILE_BYTES — a generous pre-filter on the RAW file.size, applied BEFORE
//     reading/decoding the file into memory. It is intentionally larger than the base64
//     cap so large source photos (which downscale fine to well under the payload limit)
//     are still admitted, while genuinely huge files are rejected before they cost memory.
//  2) MAX_IMAGE_BASE64_BYTES — the AUTHORITATIVE post-encode check on the actual base64
//     payload length that will be sent to API Gateway/Lambda.
const MAX_RAW_FILE_BYTES = 12 * 1024 * 1024; // ~12MB raw-file memory pre-filter (pre-decode)
const MAX_IMAGE_BASE64_BYTES = 4 * 1024 * 1024; // ~4MB base64 payload per image (API GW/Lambda limit)
const MAX_TOTAL_BODY_BYTES = 9 * 1024 * 1024; // headroom under API GW's 10MB body limit (message + images + history + JSON framing)
// Decompression-bomb guard: cap total pixels so a small file can't decode into a
// huge canvas allocation. 4096×4096 ≈ 16.7M px is still generous for screenshots
// (100M px would have already allocated ~400MB before this check).
const MAX_IMAGE_PIXELS = 4096 * 4096;
const JPEG_QUALITY = 0.9; // canvas JPEG re-encode quality (0–1)
const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'] as const;
type ImageMediaType = typeof ALLOWED_IMAGE_TYPES[number];
const isAllowedImageType = (t: string): t is ImageMediaType =>
  (ALLOWED_IMAGE_TYPES as readonly string[]).includes(t);

// Stateless UTF-8 encoder, reused across sendMessage calls. Created LAZILY on first
// use (NOT at module load): the jsdom test env has no global TextEncoder, so building
// it at import time crashes every test suite that imports this module.
let _textEncoder: TextEncoder | undefined;
const getTextEncoder = (): TextEncoder => (_textEncoder ??= new TextEncoder());

/** Collision-resistant local attachment id. Prefers crypto.getRandomValues (in the
 * standard DOM lib typings and available in insecure/HTTP dev contexts); crypto.randomUUID
 * is NOT in this project's TS lib and fails the webpack/ts-loader build, so it is avoided.
 * Falls back to Math.random only if the crypto API is unexpectedly unavailable. */
const genAttachmentId = (): string => {
  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    console.warn('genAttachmentId: crypto unavailable, using fallback', e);
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
};

/** Choose the output encoding for a canvas re-encode, preserving transparency.
 * GIF can carry transparency, so a downscaled GIF becomes PNG (not JPEG) to avoid
 * flattening transparent regions to black; PNG/WebP keep their type; JPEG -> JPEG. */
const OUTPUT_FORMAT_BY_SOURCE: Record<ImageMediaType, ImageMediaType> = {
  'image/png': 'image/png',
  'image/webp': 'image/webp',
  'image/gif': 'image/png', // GIF -> PNG to preserve transparency
  'image/jpeg': 'image/jpeg',
};
const selectOutputFormat = (sourceType: ImageMediaType): ImageMediaType =>
  OUTPUT_FORMAT_BY_SOURCE[sourceType];

/** Draw an image onto a scaled canvas and return the resulting data URL. */
const downscaleViaCanvas = (img: HTMLImageElement, outType: ImageMediaType, scale: number): string => {
  const targetW = Math.max(1, Math.round(img.width * scale));
  const targetH = Math.max(1, Math.round(img.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageAttachError('Could not process image (canvas unavailable).');
  ctx.drawImage(img, 0, 0, targetW, targetH);
  try {
    // Safari <16 silently encodes an 'image/webp' request as PNG. That's fine here:
    // splitDataUrl reads the ACTUAL resulting header (so mediaType stays correct) and
    // the post-encode size check runs on the real bytes.
    const dataUrl = outType === 'image/jpeg'
      ? canvas.toDataURL('image/jpeg', JPEG_QUALITY)
      : canvas.toDataURL(outType);
    // iOS Safari returns 'data:,' (~6 chars) when the canvas exceeds an internal pixel
    // limit instead of throwing — treat an implausibly short result as a hard failure.
    if (dataUrl.length < 100) {
      throw new ImageAttachError('Could not process image (exceeds browser canvas limit).');
    }
    return dataUrl;
  } finally {
    // Release the canvas GPU/backing memory promptly (large screenshots can hold MBs),
    // even if toDataURL throws above.
    canvas.width = 0;
    canvas.height = 0;
  }
};

/**
 * Validate, downscale (canvas, max ~1568px) and re-encode an image file.
 * Returns the base64 payload WITHOUT the data-URL prefix.
 * Throws ImageAttachError with a user-friendly message on any failure.
 */
const processImageFile = async (file: File): Promise<AttachedImage> => {
  const sourceType = file.type;
  if (!isAllowedImageType(sourceType)) {
    throw new ImageAttachError(`Unsupported image type${sourceType ? `: ${sourceType}` : ''}. Use PNG, JPEG, GIF or WebP.`);
  }

  // Stage 1 (pre-filter, NOT authoritative): reject a raw file already over the generous
  // MAX_RAW_FILE_BYTES budget before reading/decoding it into memory. This is a memory
  // guard, deliberately larger than the base64 cap — large source photos pass here and are
  // downscaled below, then re-checked against the authoritative MAX_IMAGE_BASE64_BYTES.
  if (file.size > MAX_RAW_FILE_BYTES) {
    throw new ImageAttachError('Image is too large — please attach a smaller file.');
  }

  const originalDataUrl = await readFileAsDataUrl(file);
  const img = await loadImageElement(originalDataUrl);
  if (!img.width || !img.height) {
    throw new ImageAttachError('Invalid image dimensions.');
  }
  // Accepted, tab-sandboxed risk: this pixel-count guard necessarily runs AFTER the
  // browser decodes the bitmap (dimensions aren't known pre-decode without a header
  // parser). The ≤4MB raw-file cap above bounds most inputs; a pathological, highly
  // compressed image could still briefly allocate a large bitmap, contained to this
  // tab. A createImageBitmap({ resizeWidth }) rewrite would prevent it but can't
  // preserve GIF animation, so it is deferred.
  if (img.width * img.height > MAX_IMAGE_PIXELS) {
    throw new ImageAttachError('Image dimensions are too large. Please resize before attaching.');
  }
  const largest = Math.max(img.width, img.height);
  const needsDownscale = largest > MAX_IMAGE_DIMENSION;

  // Any image that already fits keeps its ORIGINAL bytes — no wasteful canvas
  // re-encode (which would also cost JPEG quality and, for GIFs, animation). Only
  // downscale (re-encoding) when the longest edge actually exceeds the limit.
  const finalDataUrl = !needsDownscale
    ? originalDataUrl
    : downscaleViaCanvas(img, selectOutputFormat(sourceType), MAX_IMAGE_DIMENSION / largest);

  // Release the decoded bitmap's memory now that finalDataUrl is settled.
  img.src = '';

  // Parse the ACTUAL resulting media type (canvas may fall back to PNG for unsupported types).
  const { mediaType, data } = splitDataUrl(finalDataUrl);
  const finalMediaType: ImageMediaType = isAllowedImageType(mediaType) ? mediaType : 'image/png';

  // Stage 2 (authoritative): base64 string length ≈ payload byte size; API Gateway/Lambda
  // have a hard body limit, so this is the definitive per-image size check on the real bytes.
  if (data.length > MAX_IMAGE_BASE64_BYTES) {
    throw new ImageAttachError('Image is too large to send even after resizing — please attach a smaller image.');
  }

  return {
    id: genAttachmentId(),
    mediaType: finalMediaType,
    data,
  };
};

// ============================================================================
// Constants
// ============================================================================
const HEADER_COLOR = 'orange';
const HEADER_COLOR_DARK = '#e69500';
const USER_BUBBLE_COLOR = '#d48800';
const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = 700;
const DEFAULT_PANEL_WIDTH = 440;
const MAX_HISTORY_TURNS = 20;
const THUMB_SIZE = 48; // px — attachment thumbnail preview
const REMOVE_BTN_SIZE = 18; // px — thumbnail remove (×) button

// ============================================================================
// Types
// ============================================================================
interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  isError?: boolean; // True for error messages — don't show feedback buttons
  requestId?: string; // The async requestId that produced this response (for feedback)
  imageCount?: number; // Number of images the user attached to this turn (display-only)
}

/** A client-side prepared image attachment ready to send to the chat API. */
interface AttachedImage {
  id: string; // stable key for React lists + removal
  mediaType: ImageMediaType; // one of ALLOWED_IMAGE_TYPES
  data: string; // base64 payload WITHOUT the "data:...;base64," prefix
}

type FeedbackState = 'none' | 'submitting' | 'downvote-pending' | 'submitted' | 'error';

interface FeedbackEntry {
  state: FeedbackState;
  type?: 'positive' | 'negative';
}

export interface ChatWidgetProps {
  /**
   * Called when the panel opens/closes or resizes.
   * @param panelWidth Current panel width in px (0 = closed).
   * @param isResizing True while the user is actively dragging the resize handle.
   */
  onLayoutChange?: (panelWidth: number, isResizing: boolean) => void;
}

interface PollResult {
  content: string;
  isError: boolean;
}

// ============================================================================
// Styles
// ============================================================================
const useStyles = makeStyles((theme) =>
  createStyles({
    fab: {
      position: 'fixed',
      bottom: 24,
      right: 24,
      zIndex: theme.zIndex.appBar + 1, // above app bar, below drawer/modal
      backgroundColor: HEADER_COLOR,
      color: '#fff',
      '&:hover': { backgroundColor: HEADER_COLOR_DARK },
    },
    sidePanel: {
      position: 'fixed',
      top: 0,
      right: 0,
      height: '100vh',
      zIndex: theme.zIndex.appBar, // same layer as app bar
      display: 'flex',
      flexDirection: 'column',
      borderRadius: 0,
      overflow: 'hidden',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.12)',
      borderLeft: '2px solid #e0e0e0',
    },
    resizeHandle: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: 6,
      height: '100%',
      cursor: 'col-resize',
      zIndex: theme.zIndex.appBar + 1, // above panel content
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.15s',
      '&:hover': {
        backgroundColor: 'rgba(255,165,0,0.25)',
      },
      '&::after': {
        content: '""',
        display: 'block',
        width: 2,
        height: 40,
        borderRadius: 2,
        backgroundColor: '#ccc',
        transition: 'background-color 0.15s',
      },
      '&:hover::after': {
        backgroundColor: HEADER_COLOR,
      },
    },
    header: {
      backgroundColor: HEADER_COLOR,
      color: '#fff',
      padding: '14px 18px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      minHeight: 56,
    },
    headerTitle: {
      fontWeight: 600,
      fontSize: 16,
    },
    headerSubtitle: {
      fontSize: 11,
      opacity: 0.85,
    },
    messagesContainer: {
      flex: 1,
      overflowY: 'auto',
      padding: 18,
      backgroundColor: '#f5f5f5',
    },
    messageBubble: {
      maxWidth: '85%',
      padding: '12px 16px',
      borderRadius: 12,
      marginBottom: 12,
      fontSize: 13,
      lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    userMessage: {
      backgroundColor: USER_BUBBLE_COLOR,
      color: '#fff',
      marginLeft: 'auto',
      borderBottomRightRadius: 4,
    },
    assistantMessage: {
      backgroundColor: '#fff',
      color: '#333',
      border: '1px solid #e0e0e0',
      borderLeft: `3px solid ${HEADER_COLOR}`,
      borderBottomLeftRadius: 4,
      // Allow markdown content to render with proper whitespace
      whiteSpace: 'normal',
      '& p': { margin: '4px 0' },
      '& ul, & ol': { margin: '4px 0', paddingLeft: 20 },
      '& code': {
        backgroundColor: '#f0f0f0',
        padding: '1px 4px',
        borderRadius: 3,
        fontSize: 12,
      },
      '& pre': {
        backgroundColor: '#f0f0f0',
        padding: 8,
        borderRadius: 4,
        overflow: 'auto',
        fontSize: 12,
      },
      '& a': { color: HEADER_COLOR_DARK },
    },
    inputArea: {
      padding: '12px 14px',
      borderTop: '1px solid #e0e0e0',
      display: 'flex',
      alignItems: 'center',
      backgroundColor: '#fff',
    },
    textField: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: 20,
        fontSize: 13,
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: HEADER_COLOR,
      },
    },
    sendButton: {
      marginLeft: 8,
      color: HEADER_COLOR,
      '&:hover': { backgroundColor: 'rgba(255,165,0,0.1)' },
    },
    loadingDots: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      padding: '12px 16px',
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderLeft: `3px solid ${HEADER_COLOR}`,
      borderRadius: 12,
      borderBottomLeftRadius: 4,
      marginBottom: 12,
      maxWidth: '85%',
    },
    welcomeMessage: {
      textAlign: 'center',
      color: '#888',
      fontSize: 12,
      marginBottom: 16,
      padding: '0 20px',
    },
    attachButton: {
      marginRight: 4,
      color: '#888',
      '&:hover': { backgroundColor: 'rgba(255,165,0,0.1)', color: HEADER_COLOR },
    },
    attachmentsBar: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: 8,
      padding: '8px 14px 0',
      backgroundColor: '#fff',
    },
    thumbWrapper: {
      position: 'relative',
      width: THUMB_SIZE,
      height: THUMB_SIZE,
      borderRadius: 6,
      overflow: 'hidden',
      border: '1px solid #e0e0e0',
      flexShrink: 0,
    },
    thumbImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    thumbRemove: {
      position: 'absolute',
      top: 0,
      right: 0,
      width: REMOVE_BTN_SIZE,
      height: REMOVE_BTN_SIZE,
      padding: 0,
      lineHeight: '16px',
      fontSize: 13,
      color: '#fff',
      backgroundColor: 'rgba(0,0,0,0.55)',
      border: 'none',
      borderBottomLeftRadius: 6,
      cursor: 'pointer',
      '&:hover': { backgroundColor: 'rgba(0,0,0,0.75)' },
    },
    attachError: {
      color: '#d32f2f',
      fontSize: 11,
      padding: '6px 14px 0',
      backgroundColor: '#fff',
    },
    imageCountLabel: {
      fontSize: 11,
      opacity: 0.85,
      marginTop: 6,
    },
    // Applied alongside imageCountLabel when the bubble has no text (no gap above).
    imageCountLabelNoMargin: {
      marginTop: 0,
    },
  })
);

// Feedback endpoint: CHAT_API_URL ends in /chat; backend path is /chat/feedback
const FEEDBACK_API_URL = CHAT_API_URL ? CHAT_API_URL.replace(/\/chat$/, '/chat/feedback') : undefined;

// Shared style for feedback thumb buttons (static — no dynamic values)
const THUMB_BTN_STYLE: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 18,
  padding: '4px 8px',
  borderRadius: 4,
  opacity: 0.5,
  transition: 'opacity 0.15s',
};

// ============================================================================
// Component
// ============================================================================
const ChatWidget: React.FC<ChatWidgetProps> = ({ onLayoutChange }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  // INVARIANT: messages array is append-only (no delete/reorder); index keys are stable.
  const [feedbackState, setFeedbackState] = useState<Record<number, FeedbackEntry>>({});
  const [downvoteComment, setDownvoteComment] = useState<Record<number, string>>({});
  // Image attachments staged for the next message (max MAX_IMAGES)
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isResizing = useRef(false);

  // Mirror attachedImages in a ref so async add/paste handlers read the current count
  // without a stale closure (capacity checks happen mid-flight).
  const attachedImagesRef = useRef<AttachedImage[]>([]);
  useEffect(() => { attachedImagesRef.current = attachedImages; }, [attachedImages]);
  // Synchronous in-flight reservation so concurrent add calls (paste + file-picker)
  // don't both decode images that will be discarded by the MAX_IMAGES cap.
  const pendingImageCountRef = useRef(0);

  // Keep refs in sync so event handlers always read current values (no stale closures)
  const panelWidthRef = useRef(panelWidth);
  useEffect(() => { panelWidthRef.current = panelWidth; }, [panelWidth]);

  const onLayoutChangeRef = useRef(onLayoutChange);
  useEffect(() => { onLayoutChangeRef.current = onLayoutChange; }, [onLayoutChange]);

  // Abort controller for cancelling in-flight requests on unmount or panel close
  const abortControllerRef = useRef<AbortController | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  // Notify parent of layout changes (only when not actively resizing)
  useEffect(() => {
    if (!isResizing.current) {
      onLayoutChangeRef.current?.(open ? panelWidth : 0, false);
    }
  }, [open, panelWidth]);

  // Abort in-flight requests and clean up resize listeners on unmount
  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
      cleanupResizeRef.current?.();
      // Staged image state is dropped automatically when React unmounts the
      // component; no explicit clear needed (data-URL previews hold no external
      // handle such as an object URL to revoke).
    };
  }, []);

  // ---- Resize logic ----
  // Listeners are attached only during active drag to avoid idle overhead.
  // Panel width is applied via ref during drag to avoid re-rendering message bubbles per frame.
  const panelRef = useRef<HTMLDivElement>(null);
  // Ref to hold cleanup function for resize listeners, callable from unmount effect
  const cleanupResizeRef = useRef<(() => void) | null>(null);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (ev: MouseEvent) => {
      const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, window.innerWidth - ev.clientX));
      panelWidthRef.current = newWidth;
      // Apply width directly to DOM to avoid re-rendering all messages per frame
      if (panelRef.current) {
        panelRef.current.style.width = `${newWidth}px`;
      }
      onLayoutChangeRef.current?.(newWidth, true);
    };

    const cleanup = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      cleanupResizeRef.current = null;
    };

    const handleMouseUp = () => {
      cleanup();
      // Commit final width to React state — the useEffect will notify the parent
      setPanelWidth(panelWidthRef.current);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    cleanupResizeRef.current = cleanup;
  }, []);

  // ---- Open / Close ----
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    abortControllerRef.current?.abort();
    setLoading(false);
    clearAttachments();
    setOpen(false);
  };

  // ---- Image attachment logic ----
  const clearAttachments = () => {
    setAttachedImages([]);
    setAttachError(null);
  };

  const removeImage = (id: string) => {
    setAttachedImages(prev => prev.filter(img => img.id !== id));
    setAttachError(null);
  };

  // Process + stage image files from a paste or file picker, enforcing the MAX_IMAGES cap.
  const addImageFiles = useCallback(async (files: File[]) => {
    const imageFiles = files.filter(f => ALLOWED_IMAGE_TYPES.includes(f.type as ImageMediaType));
    if (imageFiles.length === 0) return;

    // Reserve capacity synchronously across concurrent calls: attachedImagesRef only
    // reflects COMMITTED state (synced post-render), so a dedicated in-flight counter
    // is what stops a rapid paste + file-picker burst from decoding images the
    // MAX_IMAGES cap would then discard. Reservation is released in the finally below.
    const remaining = MAX_IMAGES - attachedImagesRef.current.length - pendingImageCountRef.current;
    if (remaining <= 0) {
      setAttachError(`You can attach at most ${MAX_IMAGES} images.`);
      return;
    }

    setAttachError(null);
    const accepted = imageFiles.slice(0, remaining);
    const overflow = imageFiles.length - accepted.length;
    const errors: string[] = [];

    pendingImageCountRef.current += accepted.length; // reserve slots for this batch
    let releasedEarly = 0;
    try {
      for (const file of accepted) {
        try {
          const processed = await processImageFile(file);
          // Re-check capacity inside the functional update to stay safe under rapid calls.
          setAttachedImages(prev => (prev.length >= MAX_IMAGES ? prev : [...prev, processed]));
        } catch (e) {
          // Release this file's reserved slot immediately so a failed decode doesn't
          // hold capacity for the whole batch and wrongly reject concurrent valid attaches.
          pendingImageCountRef.current -= 1;
          releasedEarly += 1;
          if (!(e instanceof ImageAttachError)) {
            // Preserve a diagnostic signal for unexpected failures (canvas/OOM/decode
            // timeout) without exposing internals in the user-facing message.
            console.error('Image attach failed:', e);
          }
          errors.push(e instanceof ImageAttachError ? e.message : 'Could not attach an image.');
        }
      }
    } finally {
      pendingImageCountRef.current -= (accepted.length - releasedEarly); // release still-held reservations
    }

    if (overflow > 0) {
      errors.push(`Only ${MAX_IMAGES} images can be attached; ${overflow} extra ${overflow === 1 ? 'was' : 'were'} ignored.`);
    }
    if (errors.length > 0) setAttachError(errors.join(' '));
  }, []); // deps intentionally empty: only stable refs (attachedImagesRef, pendingImageCountRef) and setters are captured

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file' && ALLOWED_IMAGE_TYPES.includes(item.type as ImageMediaType)) {
        const file = item.getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length > 0) {
      // Only suppress the default paste when the clipboard has NO text — otherwise a
      // rich-text paste carrying both text and an image would silently lose the text.
      const hasText = Array.from(items).some(it => it.kind === 'string');
      if (!hasText) e.preventDefault();
      void addImageFiles(files);
    }
  }, [addImageFiles]);

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      void addImageFiles(Array.from(fileList));
    }
    e.target.value = ''; // reset so the same file can be re-selected later
  };

  // ---- Chat logic ----
  const pollForResult = async (requestId: string, signal: AbortSignal): Promise<PollResult> => {
    if (!CHAT_API_URL) return { content: 'Chat is not configured for this environment.', isError: true };
    const pollUrl = `${CHAT_API_URL}?requestId=${requestId}`;
    for (let i = 0; i < 40; i++) { // poll up to ~2 minutes
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      await new Promise(r => setTimeout(r, 3000));
      try {
        const token = await getAuthToken();
        const res = await fetch(pollUrl, {
          signal,
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) {
          // Client errors (4xx) are non-retryable — fail fast
          if (res.status >= 400 && res.status < 500) {
            return { content: `Request failed: ${res.status} ${res.statusText}`, isError: true };
          }
          continue; // retry on 5xx
        }
        const data = await res.json();
        if (data.status === 'complete') return { content: data.response || 'No response received.', isError: false };
        if (data.status === 'error') return { content: data.response || 'Processing failed.', isError: true };
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') throw err;
        if (err instanceof AuthError) throw err; // fail fast on auth errors
        /* retry on transient network errors */
      }
    }
    return { content: 'Request timed out. Please try again.', isError: true };
  };

  const sendMessage = async () => {
    const text = input.trim();
    const imagesToSend = attachedImages.slice(0, MAX_IMAGES);
    // Allow sending with text OR at least one image (screenshot-only question).
    if ((!text && imagesToSend.length === 0) || loading || !CHAT_API_URL) return;

    // Aggregate body-size guard: even when each image passes the per-image 4MB check,
    // 3 images + message + history can exceed API Gateway's 10MB body limit, producing a
    // silent HTTP 413 surfaced as a generic error. Reject up front so the user can adjust.
    // base64 string length ≈ payload byte size; text/history lengths approximate their JSON size.
    // Use UTF-8 byte length (not String.length / UTF-16 units) for text + history so
    // CJK/emoji-heavy content is measured accurately against the byte-based body cap.
    const historyItems = messages.slice(-MAX_HISTORY_TURNS);
    const encoder = getTextEncoder();
    const historyBytes = historyItems
      .reduce((sum, m) => sum + encoder.encode(m.content).length, 0);
    // Account for JSON framing overhead the raw byte counts omit: ~120 bytes per image
    // (media_type/data keys, quotes, braces, commas) + ~40 bytes per history item
    // (role/content keys + punctuation). This keeps the estimate conservative so we
    // reject before API Gateway silently returns a 413.
    const framingBytes = imagesToSend.length * 120 + historyItems.length * 40;
    const totalBodyBytes =
      encoder.encode(text).length +
      historyBytes +
      imagesToSend.reduce((sum, img) => sum + img.data.length, 0) + // base64 is ASCII: length == bytes
      framingBytes;
    if (totalBodyBytes > MAX_TOTAL_BODY_BYTES) {
      // Return early WITHOUT clearing the staged text/images so the user can remove one.
      setAttachError('Total attachment size is too large — remove an image and try again.');
      return;
    }

    // Cancel any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const userMsg: Message = {
      role: 'user',
      content: text,
      timestamp: new Date(),
      imageCount: imagesToSend.length || undefined,
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    clearAttachments();
    setLoading(true);

    try {
      // Build conversation history for multi-turn context (capped to avoid large payloads).
      // Current message is sent separately via `message`, so exclude it from history.
      // Reuse the historyItems slice above (DRY) and drop empty-content turns —
      // image-only turns store content: "" locally, and an empty history entry is
      // invalid for Bedrock; this mirrors the backend's own empty-turn skip.
      const history = historyItems
        .filter(m => m.content)
        .map(m => ({ role: m.role, content: m.content }));

      const token = await getAuthToken();
      const response = await fetch(CHAT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: text,
          history,
          ...(imagesToSend.length > 0
            ? { images: imagesToSend.map(img => ({ media_type: img.mediaType, data: img.data })) }
            : {}),
        }),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      let assistantContent: string;
      let messageRequestId: string | undefined;
      let isPollError = false;

      if (data.requestId) {
        // Async mode — poll for result
        messageRequestId = data.requestId;
        const pollResult = await pollForResult(data.requestId, controller.signal);
        assistantContent = pollResult.content;
        isPollError = pollResult.isError;
      } else if (data.response) {
        // Sync mode (direct response)
        assistantContent = data.response;
      } else {
        throw new Error(data.error || 'Unknown error');
      }

      const assistantMsg: Message = {
        role: 'assistant',
        content: assistantContent,
        timestamp: new Date(),
        requestId: messageRequestId,
        isError: isPollError,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return; // Component closed, skip state updates
      console.error('ChatWidget: sendMessage failed', err);
      const errorContent = err instanceof AuthError
        ? 'Your session has expired. Please refresh the page and sign in again.'
        : 'Sorry, I encountered an error. Please try again.';
      const errorMsg: Message = {
        role: 'assistant',
        content: errorContent,
        timestamp: new Date(),
        isError: true,
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ---- Feedback logic ----
  const feedbackTimersRef = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  // Clean up all feedback timers on unmount
  useEffect(() => {
    return () => {
      Object.values(feedbackTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const submitFeedback = async (msgIndex: number, type: 'positive' | 'negative', comment: string = '') => {
    if (!FEEDBACK_API_URL) return;

    // Capture message content at call time to avoid stale closure on re-index
    const targetMsg = messages[msgIndex];
    const userMsgIndex = msgIndex - 1;
    const userMsg = userMsgIndex >= 0 ? messages[userMsgIndex] : null;
    const question = userMsg?.role === 'user' ? userMsg.content : '';
    const responseText = targetMsg?.content || '';
    const requestId = targetMsg?.requestId || '';

    // Show 'submitting' state (buttons disabled, no "Thanks" yet)
    setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'submitting', type } }));

    try {
      const token = await getAuthToken();
      const res = await fetch(FEEDBACK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          feedback: type,
          question,
          response: responseText,
          comment,
          requestId,
        }),
      });

      if (res.status === 403) {
        // 403 = endpoint not deployed yet / CORS — silently mark submitted
        setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'submitted', type } }));
        return;
      }
      if (res.status === 400) {
        // 400 = validation error (e.g. response too short) — log but don't show error to user
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Hoopoe Feedback] 400 validation error — feedback not saved');
        }
        setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'submitted', type } }));
        return;
      }
      if (!res.ok) {
        setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'error', type } }));
        // Revert to 'none' after 3s so the user can retry
        feedbackTimersRef.current[msgIndex] = setTimeout(() => {
          setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'none' } }));
          delete feedbackTimersRef.current[msgIndex];
        }, 3000);
        return;
      }
      // Success
      setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'submitted', type } }));
    } catch (e) {
      // Network/CORS failures — silently mark as submitted (non-critical feature)
      if (process.env.NODE_ENV !== 'production') {
        console.debug('[Hoopoe Feedback] error:', e);
      }
      setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'submitted', type } }));
    }
  };

  const handleThumbsUp = (msgIndex: number) => {
    void submitFeedback(msgIndex, 'positive');
  };

  const handleThumbsDown = (msgIndex: number) => {
    // Show the comment input
    setFeedbackState(prev => ({ ...prev, [msgIndex]: { state: 'downvote-pending', type: 'negative' } }));
  };

  const handleDownvoteSubmit = (msgIndex: number) => {
    const comment = downvoteComment[msgIndex] || '';
    void submitFeedback(msgIndex, 'negative', comment);
  };

  // ---- Feedback rendering helper ----
  const renderFeedback = (idx: number) => {
    const fb = feedbackState[idx];
    const state = fb?.state || 'none';

    if (state === 'submitting') {
      return (
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12, marginTop: -8, paddingRight: 4, textAlign: 'right' }}>
          Sending...
        </div>
      );
    }

    if (state === 'submitted') {
      return (
        <div style={{ fontSize: 11, color: '#888', marginBottom: 12, marginTop: -8, paddingLeft: 4 }}>
          {fb?.type === 'positive' ? '👍' : '👎'} Thanks for your feedback!
        </div>
      );
    }

    if (state === 'error') {
      return (
        <div style={{ fontSize: 11, color: '#d32f2f', marginBottom: 12, marginTop: -8, paddingLeft: 4 }}>
          Feedback not saved
        </div>
      );
    }

    if (state === 'downvote-pending') {
      return (
        <div style={{ marginBottom: 12, marginTop: -4, paddingLeft: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
            <input
              type="text"
              placeholder="What was wrong? (optional)"
              aria-label="Feedback comment"
              value={downvoteComment[idx] || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              setDownvoteComment(prev => ({ ...prev, [idx]: val }));
            }}
              style={{ fontSize: 11, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4, outline: 'none', width: 200 }}
              onKeyDown={(e) => { if (e.key === 'Enter') handleDownvoteSubmit(idx); }}
            />
            <button
              onClick={() => handleDownvoteSubmit(idx)}
              style={{ fontSize: 11, padding: '4px 8px', backgroundColor: HEADER_COLOR, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
            >
              Submit
            </button>
          </div>
        </div>
      );
    }

    // state === 'none' — show buttons
    return (
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, marginTop: -8, paddingRight: 4, justifyContent: 'flex-end' }}>
        <button type="button" onClick={() => handleThumbsUp(idx)} title="Helpful" aria-label="Helpful" style={THUMB_BTN_STYLE}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}>👍</button>
        <button type="button" onClick={() => handleThumbsDown(idx)} title="Not helpful" aria-label="Not helpful" style={THUMB_BTN_STYLE}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.5'; }}>👎</button>
      </div>
    );
  };

  // Don't render the widget if no API URL is configured for this hostname
  if (!CHAT_API_URL) return null;

  return (
    <>
      {/* Floating Action Button */}
      {!open && (
        <Fab className={classes.fab} onClick={handleOpen} aria-label="Open Hoopoe">
          <ChatIcon />
        </Fab>
      )}

      {/* Side Panel */}
      {open && (
        <Paper
          ref={panelRef}
          className={classes.sidePanel}
          style={{ width: panelWidthRef.current }}
          elevation={8}
          square
        >
          {/* Drag handle for resizing */}
          <div className={classes.resizeHandle} onMouseDown={handleMouseDown} />

          {/* Header */}
          <div className={classes.header}>
            <div>
              <Typography className={classes.headerTitle}>Hoopoe</Typography>
              <Typography className={classes.headerSubtitle}>
                /ˈhuːpuː/ — AI-powered test data &amp; QA knowledge
              </Typography>
            </div>
            <IconButton size="small" onClick={handleClose} style={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </div>

          {/* Messages */}
          <div className={classes.messagesContainer}>
            {messages.length === 0 && (
              <div className={classes.welcomeMessage} style={{ textAlign: 'left' }}>
                <Typography variant="body2" style={{ marginBottom: 12, textAlign: 'center' }}>
                  👋 Hello! I'm Hoopoe — your pharmacy QA assistant.
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  I can help you with:
                </Typography>
                <Typography variant="body2" component="div" style={{ marginBottom: 12, paddingLeft: 8 }}>
                  • <b>Test customer data</b> — 92 pre-configured scenarios<br/>
                  • <b>Test coverage</b> — Query TestRail runs and results<br/>
                  • <b>QA documentation</b> — Wiki, Quip SOPs and runbooks<br/>
                  • <b>Incident triage</b> — Search open SIM/Taskei tickets<br/>
                  • <b>Code examples</b> — Find implementations in repos
                </Typography>
                <Typography variant="body2" style={{ marginBottom: 8 }}>
                  Try asking:
                </Typography>
                <Typography variant="body2" component="div" style={{ paddingLeft: 8, fontStyle: 'italic', color: '#666' }}>
                  "I need a PillPack test customer"<br/>
                  "Show me recent TestRail runs"<br/>
                  "How do I ship complete a test order?"
                </Typography>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx}>
                <div
                  className={`${classes.messageBubble} ${
                    msg.role === 'user' ? classes.userMessage : classes.assistantMessage
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                  ) : (
                    <>
                      {msg.content}
                      {msg.imageCount ? (
                        <div className={`${classes.imageCountLabel}${msg.content ? '' : ` ${classes.imageCountLabelNoMargin}`}`}>
                          📎 {msg.imageCount} image{msg.imageCount > 1 ? 's' : ''} attached
                        </div>
                      ) : null}
                    </>
                  )}
                </div>

                {/* Feedback buttons — only on assistant messages with a requestId (not errors) */}
                {msg.role === 'assistant' && !msg.isError && msg.requestId && renderFeedback(idx)}
              </div>
            ))}

            {loading && (
              <div className={classes.loadingDots}>
                <CircularProgress size={16} style={{ color: HEADER_COLOR }} />
                <Typography variant="body2" style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                  Thinking...
                </Typography>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Attachment error (invalid type / too large / over limit) */}
          {attachError && (
            <div className={classes.attachError} role="alert">{attachError}</div>
          )}

          {/* Attached image thumbnails */}
          {attachedImages.length > 0 && (
            <div className={classes.attachmentsBar}>
              {attachedImages.map((img, idx) => (
                <div key={img.id} className={classes.thumbWrapper}>
                  <img src={`data:${img.mediaType};base64,${img.data}`} alt={`Attached image ${idx + 1} of ${attachedImages.length}`} className={classes.thumbImg} />
                  <button
                    type="button"
                    className={classes.thumbRemove}
                    aria-label={`Remove attached image ${idx + 1}`}
                    title="Remove"
                    onClick={() => removeImage(img.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Input Area */}
          <div className={classes.inputArea}>
            {/* Hidden file input driven by the attach button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/gif, image/webp"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileInputChange}
              aria-hidden="true"
              tabIndex={-1}
            />
            <IconButton
              className={classes.attachButton}
              onClick={handleAttachClick}
              disabled={loading || attachedImages.length >= MAX_IMAGES}
              size="small"
              aria-label="Attach image"
              title={attachedImages.length >= MAX_IMAGES ? `Maximum ${MAX_IMAGES} images attached` : 'Attach image'}
            >
              <AttachFileIcon fontSize="small" />
            </IconButton>
            <TextField
              className={classes.textField}
              variant="outlined"
              size="small"
              placeholder="Ask me anything... (paste or attach a screenshot)"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading}
              multiline
              rowsMax={3}
              inputProps={{ onPaste: handlePaste, 'aria-label': 'Chat message' }}
            />
            <IconButton
              className={classes.sendButton}
              onClick={sendMessage}
              disabled={(!input.trim() && attachedImages.length === 0) || loading}
              size="small"
              aria-label="Send message"
            >
              <SendIcon />
            </IconButton>
          </div>
        </Paper>
      )}
    </>
  );
};

export default ChatWidget;
